import React, { useState } from 'react';
import {
  Button,
  Box,
  Typography,
  List,
  ListItem,
  ListItemText,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { AccountCircle, Lock, VpnKey, Visibility, VisibilityOff } from '@mui/icons-material';
import { resetPassword, confirmResetPassword } from 'aws-amplify/auth';
import { useNavigate } from 'react-router-dom';
import BWITextField from '../shared/BWITextField';

const passwordRules = [
  { label: "At least 8 characters", test: (pw: string) => pw.length >= 8 },
  { label: "At least 1 uppercase letter", test: (pw: string) => /[A-Z]/.test(pw) },
  { label: "At least 1 lowercase letter", test: (pw: string) => /[a-z]/.test(pw) },
  { label: "At least 1 number", test: (pw: string) => /\d/.test(pw) },
];

const ForgotPasswordContainer: React.FC = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState<1 | 2>(1);

  const [email, setEmail] = useState('');
  const [verificationCode, setVerificationCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  // Toggles
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const allValid = passwordRules.every((rule) => rule.test(newPassword));
  const passwordsMatch = newPassword === confirmPassword;

  // Step 1: Request Password Reset
  const handleResetPassword = async () => {
    try {
      const { nextStep } = await resetPassword({ username: email });

      if (nextStep.resetPasswordStep === 'CONFIRM_RESET_PASSWORD_WITH_CODE') {
        setStep(2);
      } else if (nextStep.resetPasswordStep === 'DONE') {
        navigate('/login');
      }
    } catch (error) {
      setErrorMessage('Invalid email. Please try again.');
      setEmail('');
      console.error('Reset Password Error:', error);
    }
  };

  // Step 2: Confirm Password Reset
  const handleConfirmResetPassword = async () => {
    if (!allValid) {
      setErrorMessage("New password does not meet requirements.");
      return;
    }
    if (!passwordsMatch) {
      setErrorMessage("Passwords do not match.");
      return;
    }

    try {
      await confirmResetPassword({
        username: email,
        newPassword,
        confirmationCode: verificationCode,
      });

      alert('Password reset successful! You can now log in with your new password.');
      navigate('/login');
    } catch (error) {
      setErrorMessage(`Error: ${error}`);
      console.error('Confirm Password Error:', error);
    }
  };

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        maxWidth: 600,
        backgroundColor: 'background.paper',
        padding: 3,
        borderRadius: 2,
        boxShadow: 3,
        justifyContent: 'center',
        flexDirection: 'column',
        marginTop: '3vh',
      }}
    >
      {step === 1 ? (
        // Step 1: Enter Email
        <>
          <BWITextField
            label="Recovery Email"
            type="text"
            value={email}
            onChange={(e) => setEmail(e.target.value.toLocaleLowerCase())}
            fullWidth
            margin="normal"
            startIcon={<AccountCircle />}
            helperText="Enter your email to receive a password reset code."
          />
          {errorMessage && <Typography color="error">{errorMessage}</Typography>}
          <Button
            variant="contained"
            onClick={handleResetPassword}
            sx={{
              backgroundColor: '#d32f2f',
              color: '#fff',
              marginTop: 2,
              '&:hover': { backgroundColor: '#b71c1c' },
            }}
          >
            Send Password Reset
          </Button>
        </>
      ) : (
        // Step 2: Enter Verification Code + New Password
        <>
          <BWITextField
            label="Verification Code"
            type="text"
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value)}
            fullWidth
            margin="normal"
            startIcon={<VpnKey />}
            helperText="Enter the verification code sent to your email."
          />
          <BWITextField
            label="New Password"
            type={showNew ? "text" : "password"}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            fullWidth
            margin="normal"
            startIcon={<Lock />}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowNew((prev) => !prev)} edge="end">
                    {showNew ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <BWITextField
            label="Confirm New Password"
            type={showConfirm ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            fullWidth
            margin="normal"
            startIcon={<Lock />}
            error={!!confirmPassword && !passwordsMatch}
            helperText={confirmPassword && !passwordsMatch ? "Passwords do not match" : ""}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowConfirm((prev) => !prev)} edge="end">
                    {showConfirm ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <List dense>
            {passwordRules.map((rule, i) => (
              <ListItem key={i}>
                <ListItemText
                  primary={
                    <Typography color={rule.test(newPassword) ? "green" : "red"}>
                      {rule.label}
                    </Typography>
                  }
                />
              </ListItem>
            ))}
          </List>

          {errorMessage && <Typography color="error">{errorMessage}</Typography>}
          <Button
            variant="contained"
            onClick={handleConfirmResetPassword}
            sx={{
              backgroundColor: '#d32f2f',
              color: '#fff',
              marginTop: 2,
              '&:hover': { backgroundColor: '#b71c1c' },
            }}
            disabled={!allValid || !passwordsMatch}
          >
            Confirm Reset
          </Button>
        </>
      )}
    </Box>
  );
};

export default ForgotPasswordContainer;
